import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  GET_ARSES,
  GET_BRANCH_PREREGISTRATIONS,
  GET_CHANNEL_BY_PROFILE,
  GET_DATA_MEDICAL_STAFF_PROVIDERS,
  GET_PRINT_TYPES,
  GET_PROFILES_CENTER,
  SAVE_BRANCH_USER,
  SAVE_BRANCH_USER_AND_MEDICAL,
  SAVE_PHARMACY_PROVIDER,
  SET_BRANCH_USER_ENABLED,
  GET_PROVIDER_BRANCH_NAMES,
} from "./queries";
import { useApolloClient, useMutation, useQuery } from "@apollo/react-hooks";
import { useToasts } from "react-toast-notifications";
import { useAuth0 } from "@dhn/components/contexts/react-auth0-spa";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  formularyBranch,
  formularyMedical,
  formularyProvider,
  formularyUser,
} from "./validateFormulary";
import { USER_AND_MEDICAL } from "./management/tabs/BranchTabs/subTabs/modals/queries";
import { isEmpty, omit, uniqBy } from "lodash";
import {
  buildProfileReference,
  findProfileByRecipeCapability,
} from "./utils/profile-options";
import {
  getBranchSnapshot,
  hasUnsavedChanges,
} from "./utils/unsaved-changes";
import {
  DUPLICATED_BRANCH_NAME_MESSAGE,
  hasDuplicatedBranchName,
} from "./utils/branch-office-name";
import {
  BRANCH_PREREGISTRATION_STATUSES,
  buildWorkTeamRows,
} from "./utils/work-team-rows";
import {
  ensureNoInsuredArs,
  isNoInsuredArs,
} from "./utils/branch-office-ars";

// Función auxiliar para limpiar un id (remover guiones)
const cleanId = (id) => id?.replace(/-/g, "");
const CEDULA_DOCUMENT_TYPE_ID = 2;
const PASAPORTE_DOCUMENT_TYPE_ID = 4;
const ASSOCIATED_DOCTOR_REQUIREMENTS = [
  { key: "identityCopy", label: "Copia de Cédula" },
  { key: "generalMedicalDegree", label: "Título Médico General" },
  { key: "exequaturCopy", label: "Exequátur" },
];
const MAX_ASSOCIATED_DOCTOR_DOCUMENTS_TOTAL_SIZE = 8 * 1024 * 1024;
const ALLOWED_ASSOCIATED_DOCTOR_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];

const normalizeText = (value) => (value || "").toString().trim();
const normalizeDigits = (value) => normalizeText(value).replace(/\D/g, "");
const getDocumentsTotalSize = (documents = []) =>
  documents.reduce((total, document) => total + Number(document?.size || 0), 0);
const getDocumentTypeCode = (documentTypeId) =>
  Number(documentTypeId) === PASAPORTE_DOCUMENT_TYPE_ID ? "PASAPORTE" : "CEDULA";
const buildPharmacyProviderPayload = (pharmacyProvider, params) => ({
  ...(pharmacyProvider || {}),
  ...(params
    ? { id: Number(pharmacyProvider?.id || params) }
    : {}),
});

const isAssociatedDoctorDocumentAllowed = (document = {}) =>
  Number(document.size || 0) > 0 &&
  ALLOWED_ASSOCIATED_DOCTOR_DOCUMENT_TYPES.includes(
    normalizeText(document.type).toLowerCase(),
  );

const INITIAL_STATE = {
  data: [],
  activeTab: 1,
  isLoadingDhn: false,
  modalAddAndEditIsOpen: false,
  branchSubTab: 0,
  isNew: true,
  isNewBranch: true,
  isNewUser: true,
  isNewArses: true,
  dataOptionChannel: [],
  dataOptionArs: [],
  dataOptionProfiles: [],
  branchSelected: null,
  arsesSelected: null,
  userSelected: null,
  medicalSelected: null,
  profileSelected: null,
  scheduleSelected: [],
  pharmacyProvider: {},
  branchOffice: [],
  user: [],
  medical: [],
  arses: [],
  isMedical: 0,
  showPassword: false,
  modifyTabBranch: false,
  mapDataSearch: [],
  loadingOrCreateModification: false,
  schedulingFormat: 0,
  optionsPrintTypes: [],
  issueFiscalReceipts: null,
  pendingDiscardAction: null,
};

// Convierte un registro del equipo de trabajo al formato que espera la API.
const toUserAndMedicalPayload = (item) => {
  const { specialitiesByMedical, isPendingSave, ...rest } = item;
  return {
    ...rest,
    profile: item?.profile ? buildProfileReference(item.profile) : undefined,
    documentTypeId: Number(item?.documentTypeId) || 2,
    password: item?.password || "Dhn2@22*",
  };
};

// Construye el equipo de trabajo de una sucursal a partir de los datos del servidor.
const buildBranchUsers = (data = [], dataBranch, profiles = []) => {
  // Un usuario puede tener relaciones duplicadas con la sucursal.
  const branchRelations = uniqBy(
    (data || []).filter(
      (item) => item?.branchOfficeId === dataBranch?.id && item.user,
    ),
    (item) => item.user.id,
  );
  return branchRelations.map((item) => {
    const dataMap = data.find(
      (item2) =>
        item2?.userId === item.user?.id &&
        item2?.branchOfficeId === dataBranch?.id,
    );
    return {
      id: item?.user?.id,
      email: item?.user?.email,
      profileId: dataMap?.profileId,
      username: item?.user?.username,
      lastName: item?.user?.lastName,
      firstName: item?.user?.firstName,
      enabled: true,
      identification: item?.user?.identification,
      documentTypeId: item?.user?.documentTypeId,
      profile:
        item?.profile ||
        profiles?.find((item3) => item3?.id === dataMap?.profileId),
      medicalProviderId: dataMap?.id,
      medicalStaffProviderEnabled: dataMap?.enabled,
    };
  });
};

/**
 * Hook para la vista de gestión (crear/editar) de consultorios.
 * Para la vista de listado usar use-actions-list.js.
 */
export default function useActions({ hasAccess = true } = {}) {
  const [state, setState] = useState(INITIAL_STATE);
  const { addToast } = useToasts();
  const { getTokenSilently } = useAuth0();
  const { params } = useParams();
  const history = useHistory();
  const location = useLocation();
  const branchInsuranceFocusAppliedRef = useRef(false);
  const [savePharmacy] = useMutation(SAVE_PHARMACY_PROVIDER);
  const [saveBranchUserAndMedical] = useMutation(SAVE_BRANCH_USER_AND_MEDICAL);
  const client = useApolloClient();
  const [saveBranchUser] = useMutation(SAVE_BRANCH_USER);
  const [setBranchUserEnabled] = useMutation(SET_BRANCH_USER_ENABLED);
  const providerSnapshotRef = useRef({});
  const providerLoadedRef = useRef(false);
  const branchSnapshotRef = useRef(null);
  const previousBranchSubTabRef = useRef(INITIAL_STATE.branchSubTab);
  const skip = !params || !hasAccess;
  const doctorProfile =
    useMemo(() => JSON.parse(sessionStorage.getItem("doctorProfile")) || {}, []);
  const { userId, profileId, centerId } = doctorProfile;

  // --- QUERIES ---
  const {
    data: { medicalStaffProviders } = {},
    loading: loadingMedicalStaffProviders,
    refetch: refetchMedicalStaffProviders,
  } = useQuery(GET_DATA_MEDICAL_STAFF_PROVIDERS, {
    skip,
    fetchPolicy: "network-only",
    variables: { where: { providerId: parseInt(params) } },
  });

  const { data: { userAndMedical } = {}, loading: loadingUser } = useQuery(
    USER_AND_MEDICAL,
    {
      skip: !skip || !hasAccess,
      fetchPolicy: "network-only",
      variables: { where: { id: userId || null } },
    },
  );

  const { data: { channelByProfile } = {}, loading: loadingChannelByProfile } =
    useQuery(GET_CHANNEL_BY_PROFILE, {
      skip: !hasAccess,
      fetchPolicy: "network-only",
      variables: { where: { profileId } },
    });

  const { data: { profiles } = {}, loading: loadingProfileByCenterId } =
    useQuery(GET_PROFILES_CENTER, {
      skip: !hasAccess,
      fetchPolicy: "network-only",
      variables: { where: { providerId: Number(params || centerId) || null } },
    });

  const { data: { arses } = {}, loading: loadingArses } = useQuery(GET_ARSES, {
    skip: !hasAccess,
    fetchPolicy: "network-only",
    variables: { where: { operationsCease: false } },
  });

  const { data: { getPrintType } = {}, loading: loadingPrintType } =
    useQuery(GET_PRINT_TYPES, { skip: !hasAccess });

  const branchOfficeId = Number(state.branchSelected?.id) || 0;
  const {
    data: { medicalPreregistrations: branchPreregistrations } = {},
    refetch: refetchBranchPreregistrations,
  } = useQuery(GET_BRANCH_PREREGISTRATIONS, {
    skip: !hasAccess || !params || !branchOfficeId,
    fetchPolicy: "network-only",
    variables: {
      where: {
        source: "CONSULTORY",
        providerId: Number(params),
        branchOfficeId,
        statuses: BRANCH_PREREGISTRATION_STATUSES,
      },
    },
  });

  const workTeamRows = useMemo(
    () => buildWorkTeamRows(state.user, branchPreregistrations),
    [state.user, branchPreregistrations],
  );

  // --- HANDLERS ---

  // Seguridad: si la query fue skippeada refetch puede ser undefined.
  const safeRefetchMedicalStaffProviders =
    typeof refetchMedicalStaffProviders === "function"
      ? refetchMedicalStaffProviders
      : () => Promise.resolve();

  const onChange = useCallback(
    (key, value, key2 = null, onChangeArray = false) => {
      setState((prev) => {
        if (onChangeArray) {
          return {
            ...prev,
            scheduleSelected: prev.scheduleSelected?.map((item) =>
              item.weekDay === key
                ? { ...item, edit: true, [key2]: value }
                : item,
            ),
          };
        }
        if (key2) {
          return { ...prev, [key2]: { ...prev[key2], [key]: value } };
        }
        return { ...prev, [key]: value };
      });
    },
    [],
  );

  // --- OPTIMIZACIÓN: Validación de archivos ---
  const addFile = (file) => {
    if (!file || !Array.isArray(file) || file.length === 0) {
      return addToast("Debes seleccionar un archivo.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
    if (file.length > 1) {
      return addToast("No puedes seleccionar más de un archivo.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
    if (!/^.*\.(jpg|JPG|png|PNG|jpeg|svg|SVG)$/.test(file[0].path || file[0].name)) {
      return addToast("Solo se admite el formato de archivos jpg, png y svg", {
        appearance: "error",
        autoDismiss: true,
      });
    }
    onChange("fileLogo", file[0], "pharmacyProvider");
  };

  const toggle = useCallback(
    (resetBranch) => {
      setState((prev) => ({
        ...prev,
        ...(resetBranch
          ? {
              user: null,
              arses: null,
              medical: null,
              branchSelected: null,
              branchSubTab: 0,
              arsesSelected: null,
            }
          : {}),
        modalAddAndEditIsOpen: false,
        userSelected: null,
        profileSelected: null,
        medicalSelected: null,
        isMedical: 0,
        showPassword: false,
        schedulingFormat: 0,
        scheduleSelected: [],
      }));
    },
    [],
  );

  // El equipo se guarda al momento solo cuando el consultorio y la sucursal ya existen.
  const canSaveUserImmediately =
    Boolean(params) && Boolean(state.branchSelected?.id) && !state.isNewBranch;

  const getBranchUserTarget = () => ({
    providerId: Number(params),
    branchOfficeId: Number(state.branchSelected?.id),
  });

  // Recarga el equipo de la sucursal desde el servidor tras un guardado inmediato.
  const refreshBranchUsers = async () => {
    const result = await safeRefetchMedicalStaffProviders();
    const data = result?.data?.medicalStaffProviders || state.data;
    const user = buildBranchUsers(
      data,
      state.branchSelected,
      state.dataOptionProfiles,
    );

    if (branchSnapshotRef.current) {
      branchSnapshotRef.current = { ...branchSnapshotRef.current, user };
    }
    setState((prev) => ({ ...prev, data, user, medical: [] }));
  };

  const runImmediateSave = async (request, successMessage) => {
    onChange("loadingOrCreateModification", true);
    onChange("isLoadingDhn", true);
    try {
      await request();
      await refreshBranchUsers();
      addToast(successMessage, { appearance: "success", autoDismiss: true });
      return true;
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
      return false;
    } finally {
      onChange("loadingOrCreateModification", false);
      onChange("isLoadingDhn", false);
    }
  };

  const saveUserImmediately = (record) =>
    runImmediateSave(
      () =>
        saveBranchUser({
          variables: {
            data: {
              ...getBranchUserTarget(),
              userAndMedical: toUserAndMedicalPayload(record),
            },
          },
        }),
      "Usuario guardado con éxito",
    );

  const setUserEnabledImmediately = (original) =>
    runImmediateSave(
      () =>
        setBranchUserEnabled({
          variables: {
            data: {
              ...getBranchUserTarget(),
              medicalStaffProviderId: Number(original.medicalProviderId),
              enabled: !original.medicalStaffProviderEnabled,
            },
          },
        }),
      "Estado modificado correctamente",
    );

  const onAddAndModifyUser = async (original = false) => {
    try {
      const {
        dataOptionProfiles,
        user = [],
        userSelected,
        profileSelected,
      } = state;

      if (isEmpty(userSelected?.doctorSecretary) && !original) {
        addToast("Debe tener al menos un medico asignado", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      // Función auxiliar para actualizar el estado y llamar a toggle
      const updateUsers = (newData) => {
        toggle();
        return onChange("user", newData);
      };

      // Caso cuando se pasa un objeto "original"
      if (original) {
        if (
          canSaveUserImmediately &&
          original.medicalProviderId &&
          !original.isPendingSave
        ) {
          return setUserEnabledImmediately(original);
        }

        const newData = user?.map((item) =>
          item.identification === original.identification
            ? {
                ...item,
                medicalStaffProviderEnabled:
                  !original?.medicalStaffProviderEnabled,
                edit: true,
              }
            : item,
        );
        return updateUsers(newData);
      }

      const selectedProfile = findProfileByRecipeCapability(
        dataOptionProfiles,
        profileSelected?.id || userSelected?.profileId,
        false,
      );

      if (!selectedProfile) {
        addToast(
          "Seleccione un perfil del Consultorio Médico que no emita recetas.",
          { appearance: "error", autoDismiss: true },
        );
        return false;
      }
      const profileReference = buildProfileReference(selectedProfile);

      await formularyUser.validate(userSelected);

      if (canSaveUserImmediately) {
        const saved = await saveUserImmediately({
          medicalStaffProviderEnabled: true,
          ...userSelected,
          profile: profileReference,
          edit: true,
        });
        if (saved) toggle();
        return saved;
      }

      if (
        userSelected?.id &&
        user?.some((item) => item?.id === userSelected?.id)
      ) {
        const newData = user?.map((item) =>
          item?.id === userSelected?.id
            ? { ...item, ...userSelected, profile: profileReference, edit: true }
            : item,
        );
        return updateUsers(newData);
      }

      if (user?.length) {
        const identificationExists = user?.some(
          (item) =>
            cleanId(item.identification) ===
              cleanId(userSelected.identification) && !item.id,
        );

        const newData = identificationExists
          ? user.map((item) =>
              cleanId(item.identification) ===
                cleanId(userSelected.identification) && !item.id
                ? {
                    ...item,
                    ...userSelected,
                    profile: profileReference,
                    edit: true,
                  }
                : item,
            )
          : [
              ...user,
              {
                ...userSelected,
                profile: profileReference,
                medicalStaffProviderEnabled: true,
                edit: true,
                isPendingSave: true,
              },
            ];
        return updateUsers(newData);
      }

      return updateUsers([
        {
          ...userSelected,
          profile: profileReference,
          medicalStaffProviderEnabled: true,
          edit: true,
          isPendingSave: true,
        },
      ]);
    } catch (error) {
      return addToast(`${error.message}`, {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // Arma el registro de usuario de un médico a partir del formulario del modal.
  const buildMedicalUserRecord = (property) => {
    const {
      firstName,
      midleName,
      lastName,
      lastName2,
      email,
      username,
      identificationCard,
      password,
    } = property;
    const cleanedIdentification = identificationCard?.replace(/-/g, "") || "";
    const medicalSchedule = state.scheduleSelected;

    const newUser = {
      id: state.userSelected?.id || null,
      firstName: `${firstName?.trim()} ${midleName?.trim() || ""}`.trim(),
      lastName: `${lastName?.trim()} ${lastName2?.trim() || ""}`.trim(),
      medicalStaffProviderEnabled:
        state.userSelected?.medicalStaffProviderEnabled ?? true,
      email: email || null,
      username,
      identification: cleanedIdentification,
      documentTypeId: Number(state.userSelected?.documentTypeId) || 2,
      profile: state.profileSelected,
      edit: true,
      medicalStaff: {
        ...state.medicalSelected,
        medicalSchedule,
        averageAppoimentTime:
          state?.schedulingFormat !== 2
            ? 0
            : state.medicalSelected?.averageAppoimentTime,
      },
      password,
    };

    const existingUser = state.user?.find(
      (item) =>
        (item.identification?.replace(/-/g, "") || "") === cleanedIdentification,
    );

    return existingUser
      ? {
          ...newUser,
          medicalProviderId: existingUser.medicalProviderId,
          isPendingSave: existingUser.isPendingSave,
        }
      : { ...newUser, isPendingSave: true };
  };

  const payloadUser = (property) => {
    const record = buildMedicalUserRecord(property);
    const isSameUser = (item) =>
      item?.identification?.replace(/-/g, "") === record.identification;

    if (state.user?.some(isSameUser)) {
      return onChange(
        "user",
        state.user.map((item) => (isSameUser(item) ? record : item)),
      );
    }

    return onChange("user", [...(state.user || []), record]);
  };

  const submitAssociatedDoctorPreregistration = async (data, documents) => {
    const accessToken = await getTokenSilently();
    const formData = new FormData();
    formData.append("payload", JSON.stringify({ data }));

    documents.forEach((document) => {
      if (document?.file) {
        formData.append(
          "documents",
          document.file,
          normalizeText(document?.name || document?.file?.name),
        );
      }
    });

    const response = await fetch(
      `${process.env.REACT_APP_APOLLO_CLIENT_URI}/medical-preregistrations/associated-doctor`,
      {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      },
    );
    const result = await response.json();

    if (!response.ok || result?.success === false) {
      throw new Error(
        result?.message || "No fue posible enviar la solicitud del medico.",
      );
    }

    return result?.data;
  };

  const onAddAndModifyUserMedical = async (
    associatedRequestPayload = {},
    options = {},
  ) => {
    const showMedicalError = (message) => {
      onChange("loadingOrCreateModification", false);
        onChange("isLoadingDhn", false);
        addToast(message || "No fue posible completar la solicitud.", {
        appearance: "error",
        autoDismiss: true,
      });
      return false;
    };

    try {
      const {
        dataOptionProfiles,
        medicalSelected,
        medical = [],
        profileSelected,
      } = state;
      const isAssociatedDoctorRequest =
        state.isMedical === 2 && state.isNewUser;
      const skipPrereq = Boolean(options?.skipPrereq);
      const parsedProfileId = Number(
        profileSelected?.id || medicalSelected?.profileId || 0,
      );
      const selectedProfileId = Number.isInteger(parsedProfileId)
        ? parsedProfileId
        : 0;

      if (!selectedProfileId) {
        return showMedicalError(
          "Seleccione un perfil del Consultorio MÃ©dico habilitado para emitir recetas.",
        );
      }

      await formularyMedical.validate({
        ...medicalSelected,
        profileId: selectedProfileId,
      });

      const selectedProfile = findProfileByRecipeCapability(
        dataOptionProfiles,
        selectedProfileId,
        true,
      );

      if (!selectedProfile) {
        return showMedicalError(
          "Seleccione un perfil del Consultorio Médico habilitado para emitir recetas.",
        );
      }
      const profileReference = buildProfileReference(selectedProfile);

      if (!state.schedulingFormat) {
        return showMedicalError("Debe seleccionar un Formato de Agendamiento.");
      }

      if (
        state.schedulingFormat === 2 &&
        !state?.medicalSelected?.averageAppoimentTime
      ) {
        return showMedicalError(
          "Debe Digitar valor en el campo Tiempo Promedio por Cita!!.",
        );
      }

      // Ningún día puede quedar activo o con una sola hora indicada.
      const incompleteScheduleDay = (state.scheduleSelected || []).find(
        (item) =>
          (item?.enabled || item?.fromHour || item?.toHour) &&
          (!item?.fromHour || !item?.toHour),
      );
      if (incompleteScheduleDay) {
        return showMedicalError(
          `El horario del ${incompleteScheduleDay.label || "día"} debe tener hora desde y hasta.`,
        );
      }

      if (isAssociatedDoctorRequest && !skipPrereq) {
        const providerId = Number(
          params ||
            state.branchSelected?.pharmacyProviderId ||
            state.pharmacyProvider?.id ||
            0,
        );
        const branchOfficeId = Number(state.branchSelected?.id || 0);
        const documentType = getDocumentTypeCode(
          state.userSelected?.documentTypeId ||
            medicalSelected?.documentTypeId ||
            CEDULA_DOCUMENT_TYPE_ID,
        );
        const identificationCard =
          documentType === "PASAPORTE"
            ? normalizeText(
                medicalSelected?.identificationCard ||
                  state.userSelected?.identification,
              )
            : normalizeDigits(
                medicalSelected?.identificationCard ||
                  state.userSelected?.identification,
              );
        const schedule = Array.isArray(state.scheduleSelected)
          ? state.scheduleSelected
          : [];
        const enabledSchedule = schedule.filter((item) => item?.enabled);
        const documents = Array.isArray(associatedRequestPayload?.documents)
          ? associatedRequestPayload.documents
          : Array.isArray(medicalSelected?.documents)
          ? medicalSelected.documents
          : [];
        const checkedRequirements =
          associatedRequestPayload?.checkedRequirements || {};
        const specialities = Array.isArray(associatedRequestPayload?.specialities)
          ? associatedRequestPayload.specialities
          : [];
        const missingRequirements = ASSOCIATED_DOCTOR_REQUIREMENTS.filter(
          (item) => !checkedRequirements[item.key],
        );

        if (!providerId || !branchOfficeId) {
          return showMedicalError(
            "No fue posible identificar el consultorio y la sucursal para la solicitud.",
          );
        }

        if (documentType === "CEDULA" && identificationCard.length !== 11) {
          return showMedicalError("La cédula debe tener 11 dígitos.");
        }

        if (!normalizeDigits(medicalSelected?.phone)) {
          return showMedicalError("El teléfono es obligatorio.");
        }

        if (!normalizeText(medicalSelected?.address)) {
          return showMedicalError("La dirección es obligatoria.");
        }

        if (!enabledSchedule.length) {
          return showMedicalError("Debe configurar al menos un horario activo.");
        }

        if (!documents.length) {
          return showMedicalError("Debe adjuntar al menos un documento.");
        }

        if (!specialities.length) {
          return showMedicalError("Debe seleccionar al menos una especialidad.");
        }

        if (specialities.some((speciality) => !documents.some((document) => document.requirementKey === "specialtyDegreeMsp" && Number(document.specialityId) === Number(speciality.id)))) {
          return showMedicalError("Debe adjuntar una evidencia para cada especialidad seleccionada.");
        }

        if (documents.some((document) => !isAssociatedDoctorDocumentAllowed(document))) {
          return showMedicalError(
            "Solo se admiten documentos PDF, JPG o PNG.",
          );
        }

        if (getDocumentsTotalSize(documents) > MAX_ASSOCIATED_DOCTOR_DOCUMENTS_TOTAL_SIZE) {
          return showMedicalError("El total de documentos no puede superar 8 MB.");
        }

        if (missingRequirements.length) {
          return showMedicalError(
            `Debe completar el checklist de documentos: ${missingRequirements
              .map((item) => item.label)
              .join(", ")}.`,
          );
        }

        const uploadedDocuments = documents.map((document) => ({
          name: normalizeText(document?.name || document?.file?.name),
          size: Number(document?.size || document?.file?.size || 0),
          type: normalizeText(document?.type || document?.file?.type),
          requirementKey: normalizeText(document?.requirementKey),
          specialityId: Number(document?.specialityId || 0) || null,
        }));

        await submitAssociatedDoctorPreregistration({
          preregistrationId: Number(medicalSelected?.preregistrationId || 0) || null,
          requestedProfileId: selectedProfileId,
          providerId,
          branchOfficeId,
          documentType,
          identificationCard,
          firstName: normalizeText(medicalSelected?.firstName),
          middleName: normalizeText(medicalSelected?.middleName),
          lastName: normalizeText(medicalSelected?.lastName),
          lastName2: normalizeText(medicalSelected?.lastName2),
          email: normalizeText(medicalSelected?.email),
          phone: normalizeDigits(medicalSelected?.phone),
          phone2: normalizeDigits(medicalSelected?.phone2),
          cellphone: normalizeDigits(medicalSelected?.cellphone),
          exequatur: normalizeText(medicalSelected?.exequatur),
          address: normalizeText(medicalSelected?.address),
          provinceId: Number(medicalSelected?.provinceId || 0) || null,
          municipalityId:
            Number(medicalSelected?.municipalityId || 0) || null,
          sectorId: Number(medicalSelected?.sectorId || 0) || null,
          specialities: specialities.map((speciality) => ({ id: Number(speciality.id), name: normalizeText(speciality.name) })),
          medicalSchedule: schedule.map((item) => ({
            weekDay: Number(item?.weekDay || 0),
            label: normalizeText(item?.label),
            fromHour: normalizeText(item?.fromHour),
            toHour: normalizeText(item?.toHour),
            enabled: Boolean(item?.enabled),
          })),
          averageAppoimentTime:
            state.schedulingFormat === 2
              ? Number(medicalSelected?.averageAppoimentTime || 0)
              : 0,
          documents: uploadedDocuments,
          documentRequirements: ASSOCIATED_DOCTOR_REQUIREMENTS.map(
            (item) => ({
              ...item,
              checked: Boolean(checkedRequirements[item.key]),
            }),
          ),
          confirmOtherChannelRequest: Boolean(
            associatedRequestPayload?.confirmOtherChannelRequest,
          ),
        }, documents);

        addToast("Solicitud enviada para revisión", {
          appearance: "success",
          autoDismiss: true,
        });

        onChange("loadingOrCreateModification", false);
        toggle();
        if (typeof refetchMedicalStaffProviders === "function") {
          refetchMedicalStaffProviders().catch(() => {});
        }
        if (typeof refetchBranchPreregistrations === "function") {
          refetchBranchPreregistrations().catch(() => {});
        }
        return true;
      }

      if (canSaveUserImmediately) {
        const saved = await saveUserImmediately(
          buildMedicalUserRecord(medicalSelected),
        );
        if (saved) toggle();
        return saved;
      }

      const mergeRecord = (item) => {
        payloadUser(item);
        return {
          ...item,
          ...medicalSelected,
          profile: profileReference,
          edit: true,
        };
      };

      const createNewRecord = () => ({
        ...medicalSelected,
        profile: profileReference,
        medicalStaffProviderEnabled: true,
        edit: true,
      });

      let updatedMedical = [];

      if (
        medical?.some((item) => item?.id && item?.id === medicalSelected?.id)
      ) {
        updatedMedical = medical?.map((item) =>
          item?.id === medicalSelected.id ? mergeRecord(item) : item,
        );
      } else if (medical?.length > 0) {
        const identificationExists = medical?.some(
          (item) =>
            cleanId(item?.identificationCard) ===
            cleanId(medicalSelected?.identificationCard),
        );

        updatedMedical = identificationExists
          ? medical?.map((item) =>
              cleanId(item?.identificationCard) ===
              cleanId(medicalSelected?.identificationCard)
                ? mergeRecord(item)
                : item,
            )
          : [...medical, mergeRecord(createNewRecord())];
      } else {
        payloadUser(medicalSelected);
        updatedMedical = [createNewRecord()];
      }

      toggle();
      return onChange("medical", updatedMedical);
    } catch (error) {
      onChange("loadingOrCreateModification", false);
      return showMedicalError(error?.message);
    }
  };

  const onAddAndModifyArs = (original = false) => {
    if (original) {
      if (isNoInsuredArs(original.arsId)) {
        return addToast("No Asegurado es obligatorio y no puede deshabilitarse.", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      const newData = state.arses?.map((item) =>
        item.arsId === original.arsId
          ? { ...item, enabled: !original.enabled }
          : item,
      );
      return onChange("arses", newData);
    }

    if (!state.arsesSelected?.arsId) {
      return addToast("Debe seleccionar una ARS.", {
        appearance: "error",
        autoDismiss: true,
      });
    }

    if (
      !isNoInsuredArs(state.arsesSelected?.arsId) &&
      (!state.arsesSelected?.codePSS || !state.arsesSelected.codePSS.trim())
    ) {
      return addToast("Debe ingresar un código PSS.", {
        appearance: "error",
        autoDismiss: true,
      });
    }

    onChange("arsesSelected", null);
    onChange("isNewArses", true);

    if (state.arsesSelected) {
      const selectedArs = isNoInsuredArs(state.arsesSelected.arsId)
        ? {
            ...state.arsesSelected,
            codePSS: null,
            codeUserPSS: null,
            codePP: null,
            enabled: true,
          }
        : state.arsesSelected;
      const currentArses = ensureNoInsuredArs(state.arses);
      const exists = currentArses.some(
        (item) => item.arsId === state.arsesSelected.arsId,
      );
      const newData = exists
        ? currentArses.map((item) =>
            item.arsId === state.arsesSelected.arsId
              ? { ...item, ...selectedArs }
              : item,
          )
        : [...currentArses, { ...selectedArs, enabled: true }];
      return onChange("arses", newData);
    }
    return onChange("arses", ensureNoInsuredArs());
  };

  // Las sucursales en pantalla dependen del alcance del usuario (por ejemplo,
  // una secretaria solo ve su sucursal), por eso se consultan todas.
  const getProviderBranches = async () => {
    const pharmacyProviderId = parseInt(params) || 0;
    if (!pharmacyProviderId) return state.branchOffice;

    const { data: { branchOffices = [] } = {} } = await client.query({
      query: GET_PROVIDER_BRANCH_NAMES,
      fetchPolicy: "network-only",
      variables: { where: { pharmacyProviderId } },
    });
    return branchOffices;
  };

  const onAddAndModifyBranch = async () => {
    try {
      await formularyBranch.validate(state.branchSelected);
      if (hasDuplicatedBranchName(await getProviderBranches(), state.branchSelected)) {
        throw new Error(DUPLICATED_BRANCH_NAME_MESSAGE);
      }
      onChange("modifyTabBranch", true);
      await onSaveCommon(true);
    } catch (error) {
      return addToast(error.message, {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const onPayloadSaveUser = useCallback(
    (dataBranch) => {
      const user = buildBranchUsers(
        state?.data,
        dataBranch,
        state?.dataOptionProfiles,
      );

      setState((prev) => ({
        ...prev,
        branchSubTab: 1,
        branchSelected: dataBranch,
        arses: ensureNoInsuredArs(dataBranch.arses),
        user,
      }));
    },
    [state.data, state.dataOptionProfiles],
  );

  const actionButtonAddBranch = async () => {
    try {
      await formularyProvider.validate(state.pharmacyProvider);
      onChange("activeTab", 2);
      onChange("branchSubTab", 1);
    } catch (error) {
      return addToast(`Antes de Pasar a Agregar sucursal ${error.message}`, {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const onSaveCommon = async (withPharmacy = false) => {
    try {
      onChange("loadingOrCreateModification", true);
      onChange("isLoadingDhn", true);
      const pharmacyProviderPayload = buildPharmacyProviderPayload(
        state.pharmacyProvider,
        params,
      );

      await formularyProvider.validate(pharmacyProviderPayload);
      const { data: { savePharmacyProvider } = {} } = await savePharmacy({
        variables: { data: pharmacyProviderPayload },
      });

      const refreshLogoInSession = async () => {
        const lastFile = Array.isArray(pharmacyProviderPayload?.fileLogo)
          ? pharmacyProviderPayload.fileLogo[
              pharmacyProviderPayload.fileLogo.length - 1
            ]
          : pharmacyProviderPayload?.fileLogo;

        const fileName = lastFile?.name || "";
        if (!fileName) return;

        const accessToken = await getTokenSilently();
        const uri = `${process.env.REACT_APP_APOLLO_CLIENT_URI}/claim-files/${fileName}`;

        const resp = await fetch(uri, {
          body: JSON.stringify(lastFile),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
        });

        if (resp.status < 200 || resp.status > 201) {
          return;
        }

        const arrayBuffer = await resp.arrayBuffer();
        const base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(new Blob([arrayBuffer]));
        });

        sessionStorage.setItem("imageLogo", base64String);
        sessionStorage.setItem("imageLogoFileName", fileName);
        window.dispatchEvent(new Event("dhn:logo-updated"));
      };

      await refreshLogoInSession();

      if (withPharmacy || state.modifyTabBranch) {
        const payload = {
          isNewBranch: state.isNewBranch,
          isNew: state.isNew,
          branchOffice: {
            ...state.branchSelected,
            arses: ensureNoInsuredArs(state.arses),
            pharmacyProviderId: savePharmacyProvider.id,
          },
          usersAndMedical: state.user
            ?.filter((item) => item.edit)
            .map(toUserAndMedicalPayload),
        };

        await saveBranchUserAndMedical({ variables: { data: payload } });
      }
      addToast(params ? "Consultorio modificado correctamente" : "Consultorio agregado correctamente", {
        appearance: "success",
        autoDismiss: true,
      });
      history.goBack();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      onChange("loadingOrCreateModification", false);
      onChange("isLoadingDhn", false);
    }
  };

  const actionsButtonEditUser = useCallback((original) => {
    setState((prev) => ({
      ...prev,
      userSelected: original,
      medicalSelected: original?.medicalStaff,
      profileSelected: original?.medicalStaff?.profile || original?.profile,
      scheduleSelected: original?.medicalStaff?.medicalSchedule || [],
      isMedical: original?.medicalStaff ? 2 : 1,
      modalAddAndEditIsOpen: true,
      isNewUser: false,
      schedulingFormat: original?.medicalStaff?.averageAppoimentTime ? 2 : 1,
    }));
  }, []);

  const onRemoveUnsavedUser = useCallback((original) => {
    if (!original?.isPendingSave) return;

    const identification = cleanId(original.identification);
    const isSameRecord = (item) =>
      item === original ||
      (Boolean(identification) &&
        cleanId(item?.identification) === identification);

    setState((prev) => ({
      ...prev,
      user: (prev.user || []).filter(
        (item) => !(item?.isPendingSave && isSameRecord(item)),
      ),
      medical: (prev.medical || []).filter(
        (item) =>
          !identification ||
          cleanId(item?.identificationCard) !== identification,
      ),
      mapDataSearch: (prev.mapDataSearch || []).filter(
        (item) => !isSameRecord(item),
      ),
    }));
  }, []);

  const hasUnsavedBranchChanges = () =>
    Boolean(state.branchSubTab) &&
    branchSnapshotRef.current !== null &&
    hasUnsavedChanges(branchSnapshotRef.current, getBranchSnapshot(state));

  const hasUnsavedProviderChanges = () =>
    hasUnsavedChanges(providerSnapshotRef.current, state.pharmacyProvider);

  const discardActions = {
    // Sin consultorio guardado, Cancelar solo vuelve a General y conserva los datos.
    branch: () => (params ? toggle(true) : onChange("activeTab", 1)),
    back: () => history.goBack(),
  };

  const requestCancelBranch = () => {
    if (params && hasUnsavedBranchChanges()) {
      return onChange("pendingDiscardAction", "branch");
    }
    return discardActions.branch();
  };

  const requestGoBack = () => {
    if (hasUnsavedProviderChanges() || hasUnsavedBranchChanges()) {
      return onChange("pendingDiscardAction", "back");
    }
    return discardActions.back();
  };

  const confirmDiscard = () => {
    const action = discardActions[state.pendingDiscardAction];
    onChange("pendingDiscardAction", null);
    if (action) action();
  };

  const cancelDiscard = () => onChange("pendingDiscardAction", null);

  // --- EFFECTS ---
  useEffect(() => {
    const wasEditingBranch = Boolean(previousBranchSubTabRef.current);
    previousBranchSubTabRef.current = state.branchSubTab;

    if (!state.branchSubTab) {
      branchSnapshotRef.current = null;
      return;
    }

    if (!wasEditingBranch) {
      branchSnapshotRef.current = getBranchSnapshot({
        branchSelected: state.branchSelected,
        user: state.user,
        arses: state.arses,
      });
    }
  }, [state.branchSubTab, state.branchSelected, state.user, state.arses]);

  useEffect(() => {
    if (channelByProfile && !loadingChannelByProfile) {
      setState((prev) => ({ ...prev, dataOptionChannel: channelByProfile }));
    }
  }, [channelByProfile, loadingChannelByProfile]);

  useEffect(() => {
    if (medicalStaffProviders && !loadingMedicalStaffProviders) {
      setState((prev) => ({ ...prev, data: medicalStaffProviders }));
    }
  }, [medicalStaffProviders, loadingMedicalStaffProviders]);

  useEffect(() => {
    if (arses && !loadingArses) {
      setState((prev) => ({ ...prev, dataOptionArs: arses }));
    }
  }, [arses, loadingArses]);

  useEffect(() => {
    if (profiles && !loadingProfileByCenterId) {
      setState((prev) => ({ ...prev, dataOptionProfiles: profiles }));
    }
  }, [profiles, loadingProfileByCenterId]);

  useEffect(() => {
    if (userAndMedical && !loadingUser) {
      const medicalStaff =
        omit(userAndMedical.medicalStaff, ["specialitiesByMedical"]) || {};
      const specialitiesByMedical = medicalStaff.specialitiesByMedical || [];
      const specialitiesList = specialitiesByMedical.map(
        ({ speciality }) => speciality,
      );
      setState((prev) => ({
        ...prev,
        user: [
          {
            ...userAndMedical,
            medicalStaff: { ...medicalStaff, specialities: specialitiesList },
            edit: true,
          },
        ],
      }));
    }
  }, [userAndMedical, loadingUser]);

  useEffect(() => {
    providerLoadedRef.current = false;
  }, [params]);

  useEffect(() => {
    const branchOffice = state.data
      .filter((item) => item.branchOffice !== null)
      .reduce((acc, item) => {
        if (!acc.some((bo) => bo.id === item.branchOffice.id)) {
          acc.push(item.branchOffice);
        }
        return acc;
      }, []);
    if (params) {
      // Se carga una sola vez para que recargar el equipo de la sucursal
      // no descarte los cambios pendientes de la pestaña General.
      const loadProvider = !providerLoadedRef.current;
      const pharmacyProvider = state.data[0]?.pharmacyProvider || {};
      if (loadProvider) {
        providerSnapshotRef.current = pharmacyProvider;
        providerLoadedRef.current = state.data.length > 0;
      }
      setState((prev) => ({
        ...prev,
        isNew: false,
        branchOffice,
        ...(loadProvider ? { pharmacyProvider } : {}),
      }));
    }
  }, [params, state.data]);

  useEffect(() => {
    if (
      !location.state?.focusBranchInsurers ||
      branchInsuranceFocusAppliedRef.current ||
      !params ||
      !state.branchOffice?.length
    ) {
      return;
    }

    const branchOfficeId = Number(location.state?.branchOfficeId || 0);
    const branchToConfigure =
      state.branchOffice.find((branch) => Number(branch?.id) === branchOfficeId) ||
      state.branchOffice[0];

    if (!branchToConfigure) {
      return;
    }

    branchInsuranceFocusAppliedRef.current = true;
    onPayloadSaveUser(branchToConfigure);
    setState((prev) => ({
      ...prev,
      activeTab: 2,
      branchSubTab: 3,
      isNewBranch: false,
      mapDataSearch: [],
    }));
  }, [
    location.state?.branchOfficeId,
    location.state?.focusBranchInsurers,
    onPayloadSaveUser,
    params,
    state.branchOffice,
  ]);

  useEffect(() => {
    if (getPrintType && !loadingPrintType) {
      setState((prev) => ({
        ...prev,
        optionsPrintTypes: getPrintType,
      }));
    }
  }, [getPrintType, loadingPrintType]);

  return [
    { state, workTeamRows },
    {
      onChange,
      toggle,
      params,
      onPayloadSaveUser,
      addFile,
      refetchMedicalStaffProviders: safeRefetchMedicalStaffProviders,
      onAddAndModifyUser,
      onSaveCommon,
      onAddAndModifyArs,
      onAddAndModifyUserMedical,
      onAddAndModifyBranch,
      history,
      actionButtonAddBranch,
      actionsButtonEditUser,
      addToast,
      onRemoveUnsavedUser,
      requestCancelBranch,
      requestGoBack,
      confirmDiscard,
      cancelDiscard,
    },
  ];
}
